'use client';

import React from 'react';
import * as Sentry from 'utils/errorTracking';

// Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@startengine/design-system.ui.button';
import { styled } from '@mui/material/styles';
import { InfoCircleIcon } from '@startengine/design-system.ui.icons';
import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

// Styled Components aligned with the app's design system
const ComponentErrorContainer = styled(Box)(({ theme, variant }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: variant === 'compact' ? '24px 16px' : '48px 24px',
  minHeight: variant === 'compact' ? '160px' : '280px',
  backgroundColor: 'white',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.grey[200]}`,
  margin: '16px 0',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
  // Add subtle background pattern
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.error.light} 0%, ${theme.palette.error.main} 100%)`,
  },
}));

const IconWrapper = styled(Box)(({ theme, variant }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: variant === 'compact' ? '48px' : '64px',
  height: variant === 'compact' ? '48px' : '64px',
  backgroundColor: theme.palette.grey[50],
  borderRadius: '50%',
  marginBottom: variant === 'compact' ? '16px' : '24px',
  '& svg': {
    width: variant === 'compact' ? '24px' : '32px',
    height: variant === 'compact' ? '24px' : '32px',
    color: theme.palette.error.main,
  },
}));

const ComponentErrorTitle = styled(Typography)(({ theme, variant }) => ({
  fontWeight: 700,
  fontSize: variant === 'compact' ? '18px' : '24px',
  lineHeight: variant === 'compact' ? '24px' : '32px',
  textAlign: 'center',
  color: theme.palette.secondary.main,
  marginBottom: '8px',
  letterSpacing: '0.05px',
}));

const ComponentErrorMessage = styled(Typography)(({ theme, variant }) => ({
  fontWeight: 400,
  fontSize: variant === 'compact' ? '14px' : '16px',
  lineHeight: variant === 'compact' ? '20px' : '24px',
  textAlign: 'center',
  color: theme.palette.common.tabsTextColor,
  marginBottom: variant === 'compact' ? '20px' : '32px',
  maxWidth: '480px',
  letterSpacing: '0.05px',
}));

class ComponentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(_error) {
    const errorId = `COMP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      errorId,
    };
  }

  componentDidCatch(error, errorInfo) {
    const { componentName, level = 'component' } = this.props;
    const { retryCount, errorId } = this.state;

    // Only send to Sentry if it's not a known third-party error
    const isThirdPartyError = this.isThirdPartyError(error);

    if (!isThirdPartyError && typeof Sentry !== 'undefined' && Sentry.captureException) {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
          component: {
            name: componentName || 'unknown',
            level,
            errorBoundary: 'ComponentErrorBoundary',
            retryCount,
          },
        },
        extra: {
          errorId,
          message: `Component-level error in ${componentName || 'unknown component'}`,
          props: this.props,
        },
        tags: {
          errorBoundary: 'component',
          componentName: componentName || 'unknown',
          errorId,
        },
      });
    }

    // Log for debugging (only in development)
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(`[ComponentErrorBoundary] Component error in ${componentName}:`, {
        error: error.message,
        errorId,
        componentName,
        level,
        retryCount,
        isThirdParty: isThirdPartyError,
      });
    }
  }

  isThirdPartyError = (error) => {
    // Check for common third-party error patterns
    const thirdPartyPatterns = [
      'webkit.messageHandlers',
      'messageHandlers',
      'ethereum',
      'chrome-extension',
      'moz-extension',
      'posthog',
      'Non-Error promise rejection captured',
    ];

    return thirdPartyPatterns.some(
      (pattern) => error?.message?.includes(pattern) || error?.stack?.includes(pattern),
    );
  };

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      errorId: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  render() {
    const { hasError, errorId, retryCount } = this.state;
    const {
      children,
      fallback,
      componentName = 'Component',
      variant = 'default', // 'default' | 'compact'
      showRetry = true,
      maxRetries = 3,
      showFallback = true,
    } = this.props;

    if (hasError) {
      // If custom fallback is provided, use it
      if (fallback) {
        return typeof fallback === 'function'
          ? fallback({ errorId, componentName, onRetry: this.handleRetry })
          : fallback;
      }

      // Opt-out for wrappers around intentionally invisible children (e.g. the
      // visually-hidden OfferingDirectory), where the default panel would make
      // a hidden component suddenly visible.
      if (!showFallback) {
        return null;
      }

      // Don't show retry button if max retries exceeded
      const canRetry = showRetry && retryCount < maxRetries;

      // Default error UI with improved design
      return (
        <ComponentErrorContainer
          variant={variant}
          data-cy="component-error-boundary"
          data-testid="component-error-boundary"
          data-component={componentName}
        >
          {/* Error Icon */}
          <IconWrapper variant={variant}>
            <InfoCircleIcon />
          </IconWrapper>

          <ComponentErrorTitle variant={variant}>
            {componentName} couldn&apos;t load
          </ComponentErrorTitle>

          <ComponentErrorMessage variant={variant}>
            We ran into an issue loading this section.
            {canRetry
              ? ' Give it another try or keep exploring other parts of the page.'
              : ' Please refresh the page or continue browsing.'}
          </ComponentErrorMessage>

          {canRetry && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size={variant === 'compact' ? 'small' : 'medium'}
                onClick={this.handleRetry}
                startIcon={<RefreshIcon sx={{ width: '16px', height: '16px' }} />}
                sx={{
                  height: variant === 'compact' ? '36px' : '44px',
                  minWidth: variant === 'compact' ? '120px' : '140px',
                  fontSize: variant === 'compact' ? '13px' : '15px',
                  backgroundColor: 'black',
                  textTransform: 'none',
                  fontWeight: 600,
                  letterSpacing: '0.05px',
                  '&:hover': {
                    backgroundColor: '#333',
                  },
                  // Icon animation on hover
                  '&:hover .MuiButton-startIcon svg': {
                    transform: 'rotate(180deg)',
                    transition: 'transform 0.3s ease',
                  },
                }}
                data-cy="component-retry-button"
                data-testid="component-retry-button"
              >
                Try Again
              </Button>
            </Box>
          )}

          {/* Error Tracking Info */}
          {(process.env.NODE_ENV === 'development' || retryCount > 0) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                marginTop: variant === 'compact' ? '16px' : '24px',
              }}
            >
              {retryCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'grey.600',
                    fontSize: '12px',
                    textAlign: 'center',
                  }}
                >
                  Retry attempts: {retryCount}/{maxRetries}
                </Typography>
              )}
              {/* Error ID for debugging (only in development) */}
              {process.env.NODE_ENV === 'development' && errorId && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'grey.500',
                    fontSize: '11px',
                    textAlign: 'center',
                    fontFamily: 'monospace',
                    padding: '4px 8px',
                    backgroundColor: 'grey.50',
                    borderRadius: '4px',
                  }}
                >
                  ID: {errorId}
                </Typography>
              )}
            </Box>
          )}
        </ComponentErrorContainer>
      );
    }

    return children;
  }
}

ComponentErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  componentName: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'compact']),
  showRetry: PropTypes.bool,
  maxRetries: PropTypes.number,
  showFallback: PropTypes.bool,
  level: PropTypes.oneOf(['component', 'section', 'feature']),
};

ComponentErrorBoundary.defaultProps = {
  fallback: null,
  componentName: 'Component',
  variant: 'default',
  showRetry: true,
  maxRetries: 3,
  showFallback: true,
  level: 'component',
};

export default ComponentErrorBoundary;
